import { Route } from "found";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getReportParams } from "../pages/reports";

import ScheduledDocumentsQuery from "./awaiting_activation/graphql/ScheduledDocumentsQuery";
import PendingPoliciesReport from "./awaiting_activation/PendingPoliciesReport";
import ScheduledDocumentsReport from "./awaiting_activation/ScheduledDocumentsReport";
import DuplicatePoliciesReport from "./duplicates/DuplicatePoliciesReport";
import DuplicateDocumentsQuery from "./duplicates/graphql/DuplicateDocumentsQuery";
import DocumentsForReviewReport from "./expiring/DocumentsForReviewReport";
import DocumentsForReviewQuery from "./expiring/graphql/DocumentsForReviewQuery";
import OverrideReportPage from "./override_report/OverrideReportPage";
import OverrideReportPageQuery from "./override_report/OverrideReportPageQuery";
import NewAndRecentlyRevisedReport from "./revised_recent/NewAndRecentlyRevisedReport";
import VergeAssociatedDocumentsQuery from "./verge_standards_associated/VergeAssociatedDocumentsQuery";
import VergeAssociatedDocumentsReport from "./verge_standards_associated/VergeAssociatedDocumentsReport";

import titleCase from "pstat-anywhere/utils/titleCase";
import { ErrorCheckWrapper } from "pstat-anywhere/utils/routes";
import {
  PageWrapper,
  ReportDocumentTitleWrapper
} from "pstat-anywhere/utils/pageWrappers";
import { useLabels } from "pstat-anywhere/context_providers/LabelContext";
import { ContentContainer } from "pstat-anywhere/components/pages";

function PolicyReportsWrapper(props) {
  return <ContentContainer>{props.children}</ContentContainer>;
}

const ScheduledDocumentsRoute = props => (
  <ErrorCheckWrapper queryName="scheduledDocuments" {...props}>
    <ReportDocumentTitleWrapper
      titleKey="documentReports.awaitingActivation.scheduled.htmlTitle"
      Component={ScheduledDocumentsReport}
      {...props}
    />
  </ErrorCheckWrapper>
);

const VergeAssociatedDocumentsRoute = props => {
  const { documentLabelPlural } = useLabels();
  const { t } = useTranslation();

  const forcedTitle = useMemo(() => {
    return t("documentReports.associatedStandards.title", {
      documentLabelPlural: titleCase(documentLabelPlural)
    });
  }, [documentLabelPlural, t]);

  return (
    <PageWrapper
      forcedTitle={forcedTitle}
      component={VergeAssociatedDocumentsReport}
      queryName="documentsWithVergeStandards"
      {...props}
    />
  );
};

const getDueForReviewParams = location => {
  const {
    categories,
    days_until_expiration,
    days_waiting_since,
    include_pending
  } = location.query;
  const { q, ...otherReportParams } = getReportParams(location);
  return {
    ...otherReportParams,
    searchQuery: q,
    categories,
    daysUntilExpiration: days_until_expiration,
    daysWaitingSince: days_waiting_since,
    includePending: include_pending
  };
};

const getScheduledReportParams = location => {
  const { q, ...otherReportParams } = getReportParams(location);
  return {
    ...otherReportParams,
    search_query: q
  };
};

const DocumentsForReviewReportWithWrappers = props => (
  <ErrorCheckWrapper queryName="documentsToReview" {...props}>
    <ReportDocumentTitleWrapper
      titleKey="documentReports.expiring.documentTitle"
      Component={DocumentsForReviewReport}
      {...props}
    />
  </ErrorCheckWrapper>
);

const OverrideReportRoute = props => (
  <ErrorCheckWrapper queryName="overrideReport" {...props}>
    <ReportDocumentTitleWrapper
      titleKey="administration.reports.overrideReport"
      Component={OverrideReportPage}
      {...props}
    />
  </ErrorCheckWrapper>
);

const routes = (
  <Route path="policy_reports" Component={PolicyReportsWrapper}>
    <Route
      path="revised/recent"
      data={{ routeName: "revised_recent_all" }}
      Component={props => (
        <ReportDocumentTitleWrapper
          titleKey="documentReports.revisedRecent.documentTitle"
          Component={NewAndRecentlyRevisedReport}
          {...props}
        />
      )}
    />
    <Route
      path="expiring"
      data={{ routeName: "expiring_all" }}
      Component={DocumentsForReviewReport}
      query={DocumentsForReviewQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getDueForReviewParams(location),
        justMine: false,
        reviewType: "TIME"
      })}
      render={({ props }) => (
        <DocumentsForReviewReportWithWrappers {...props} />
      )}
    />
    <Route
      path="expiring/mine"
      data={{ routeName: "expiring_mine" }}
      Component={DocumentsForReviewReport}
      query={DocumentsForReviewQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getDueForReviewParams(location),
        justMine: true,
        reviewType: "TIME"
      })}
      render={({ props }) => (
        <DocumentsForReviewReportWithWrappers {...props} />
      )}
    />
    <Route
      path="expiring/template"
      data={{ routeName: "expiring_templates_all" }}
      Component={DocumentsForReviewReport}
      query={DocumentsForReviewQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getDueForReviewParams(location),
        justMine: false,
        reviewType: "TEMPLATE"
      })}
      render={({ props }) => (
        <DocumentsForReviewReportWithWrappers {...props} />
      )}
    />
    <Route
      path="expiring/template/mine"
      data={{ routeName: "expiring_templates_mine" }}
      Component={DocumentsForReviewReport}
      query={DocumentsForReviewQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getDueForReviewParams(location),
        justMine: true,
        reviewType: "TEMPLATE"
      })}
      render={({ props }) => (
        <DocumentsForReviewReportWithWrappers {...props} />
      )}
    />
    <Route
      path="expiring/template/rejected"
      data={{ routeName: "expiring_templates_rejected" }}
      Component={DocumentsForReviewReport}
      query={DocumentsForReviewQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getDueForReviewParams(location),
        justMine: false,
        reviewType: "TEMPLATE_REJECTED"
      })}
      render={({ props }) => (
        <DocumentsForReviewReportWithWrappers {...props} />
      )}
    />
    <Route
      path="awaiting_activation/pending"
      data={{ routeName: "awaiting_activation_mine" }}
      Component={props => (
        <ReportDocumentTitleWrapper
          titleKey="documentReports.pending.documentTitle"
          Component={PendingPoliciesReport}
          {...props}
        />
      )}
    />
    <Route
      path="awaiting_activation/pending/all"
      data={{ routeName: "awaiting_activation_all" }}
      Component={props => (
        <ReportDocumentTitleWrapper
          titleKey="documentReports.pending.documentTitle"
          Component={PendingPoliciesReport}
          {...props}
        />
      )}
    />
    <Route
      path="awaiting_activation/scheduled"
      data={{ routeName: "scheduled_report_all" }}
      query={ScheduledDocumentsQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getScheduledReportParams(location),
        showAll: true
      })}
      render={({ props }) => (
        <ScheduledDocumentsRoute {...props} showAll={true} />
      )}
    />
    <Route
      path="awaiting_activation/scheduled/mine"
      data={{ routeName: "scheduled_report_mine" }}
      query={ScheduledDocumentsQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getScheduledReportParams(location),
        showAll: false
      })}
      render={({ props }) => (
        <ScheduledDocumentsRoute {...props} showAll={false} />
      )}
    />
    <Route
      path="duplicates"
      data={{ routeName: "duplicates_all" }}
      query={DuplicateDocumentsQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getReportParams(location)
      })}
      render={({ props }) => (
        <ErrorCheckWrapper queryName="duplicateDocuments" {...props}>
          <ReportDocumentTitleWrapper
            titleKey="documentReports.duplicates.documentTitle"
            Component={DuplicatePoliciesReport}
            {...props}
          />
        </ErrorCheckWrapper>
      )}
    />
    <Route
      path="standards/associated"
      data={{ routeName: "associated_standards_report_all" }}
      query={VergeAssociatedDocumentsQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getReportParams(location),
        justMine: false
      })}
      render={({ props }) => <VergeAssociatedDocumentsRoute {...props} />}
    />
    <Route
      path="override_report"
      data={{ routeName: "override_report" }}
      query={OverrideReportPageQuery}
      prepareVariables={(params, { location }) => ({
        ...params,
        ...getReportParams(location),
        sort: location.query.sort || "-action_date"
      })}
      render={({ props }) => <OverrideReportRoute {...props} />}
    />
  </Route>
);

export default routes;
